import { useEffect, useLayoutEffect, useRef } from 'react';
import { nowInstant } from '@insio/dates';
import { onSubscriptionsReconnected } from '@/services/subscription-reconnect';

/** A page hidden at least this long may have missed live events, so it re-reads on return. */
export const RESYNC_AFTER_HIDDEN_MS = 15_000;

/**
 * Calls `resync` when live events may have been missed: after every reconnect of the
 * subscription socket and when the page returns after a long time hidden. Never on mount.
 */
export function useLiveResync(resync: () => void): void {
  const latest = useRef(resync);
  useLayoutEffect(() => {
    latest.current = resync;
  }, [resync]);

  useEffect(() => {
    const run = () => latest.current();
    // Wall clock: a monotonic clock stops while a phone sleeps, hiding the very gap that matters.
    const now = () => nowInstant().epochMilliseconds;
    let hiddenSince = document.visibilityState === 'hidden' ? now() : null;

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        hiddenSince ??= now();
        return;
      }
      const hiddenFor = hiddenSince === null ? 0 : now() - hiddenSince;
      hiddenSince = null;
      if (hiddenFor >= RESYNC_AFTER_HIDDEN_MS) run();
    };

    const stopReconnect = onSubscriptionsReconnected(run);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopReconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
